import { useEffect, useState } from 'react'

import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";

import { IVenueGroup } from '../groupsManagement/state/groupsManagement.reducer';
import IVenueSettings from "../../interfaces/IVenueSettings";
import { useTranslation } from 'react-i18next';
import { useSnackbarMessages } from '../../yoello-lib/modules/components/snackbar/SnackbarContext';
import { useSelector } from 'react-redux';
import { IReduxAppState } from '../../reducers';
import { options } from './util/options';
import { ITippingOption, Tip } from './types';

export function useTippingOptions(venue_group_id?:string) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tippingOptions , setTippingOptions] = useState<any>(null);
  const [groupDetails , setGroupDetails] = useState<IVenueGroup | undefined>(undefined);
  const { t } = useTranslation();
  const { sendSnackbarMessage } = useSnackbarMessages()!;
  const selectedVenueId = useSelector(
    (state: IReduxAppState) => state.selectedVenueId
  );
  const venueSettings = useSelector(
    (state: IReduxAppState) => state.venue[selectedVenueId]?.venueSettings
  );

  const { callAPI: callGroups } = useAPIV2<IVenueGroup[] | IVenueGroup>(APIExtensions.venueGroups, {
    onMountDisabled: true,
  });

  const { callAPI } = useAPIV2<Tip[]>(
    APIExtensions.tipping,
    {
      params: {
        venue_id: selectedVenueId,
        include_disabled: true,
      },
      doNotCacheValue: true,
      onMountDisabled: true,
    }
  );
  const { callAPI: callAPIVenue } = useAPIV2<IVenueSettings>(
    APIExtensions.venueSettings,
    { onMountDisabled: true,    
      successCall: t('snackbar.updateSuccess'),
      successCallMethods: [HTTPMethods.PUT] }
  );

  useEffect(()=>{
    getTippingOptions();
  // eslint-disable-next-line  
  },[venueSettings])

  const createTipping = async () => {
    setIsLoading(true);
    const workload: Promise<any>[] = [];
      for (const option of options) {
        const stateObj:any = {
          enabled: false,
          sort_position: option.sort_position,
          tip_type: option.name,
        };
        if(option.name === "PERCENTAGE"){
          stateObj["value"] = 0
        }
        const { options, method } = createTippingMethods(stateObj, option, selectedVenueId, venue_group_id!!);
        if(venue_group_id){
          workload.push(callGroups(method, options));
        }else{
          workload.push(callAPI(method, options));
        }
        
      }
      await Promise.all(workload);
      
      getTippingOptions();
  }

  function createTippingMethods(formState: Tip, optionState: ITippingOption, venueId: string , venue_group_id:string) {
    const body: Record<string, any> = {
      tip_type: optionState.name,
      ...(venue_group_id ? {venue_group_id:venue_group_id } : {venue_id: venueId}),
      sort_position: optionState.sort_position,
      enabled: formState?.enabled ?? false
    };
  
    if (optionState.name === "PERCENTAGE") {
      body.value = formState?.value ?? 0;
    }
  
    const method = formState?.id
      ? HTTPMethods.PUT
      : HTTPMethods.POST;
  
    const opts = {
      body,
      query: venue_group_id ? `${venue_group_id}/tipping_options${formState?.id ? `/${formState?.id}`:''}` : formState?.id,
    };
  
    return {
      method,
      options: opts
    }
  }

  const handleSubmit = async (data: any) => {
    
    const totalAmount = data?.tipping ? Object.keys(data?.tipping).filter((key) => data.tipping[key]?.enabled).length : 0;
    if (data.tipping_enabled && totalAmount === 0 && !venue_group_id) {
      sendSnackbarMessage(t('tipping.enabledEmpty'), "error");
      return;
    }
    setIsLoading(true);
    const workload: Promise<any>[] = [];
    /**Update the tipping options if enabled */
    if (data?.tipping) {
      for (const option of options) {
        const stateObj = data.tipping[option.keyName];
        const { options, method } = createTippingMethods(stateObj, option, selectedVenueId, venue_group_id!!);
        if(venue_group_id){
          workload.push(callGroups(method, options));
        }else{
          workload.push(callAPI(method, options));
        }
      }
    }
    if (data.tipping_enabled !== undefined) {
      /** Update group settings with new enabled data */
      if(venue_group_id && groupDetails){
        workload.push(callGroups(HTTPMethods.PUT, {
          query: `${venue_group_id}/settings/${groupDetails.group_settings.id}`,
          body: {
            tipping_options_override: !!data.tipping_enabled,
            default_tipping_option_id: data.tipping.default_option_id === "1" ? null : data.tipping.default_option_id
          },
        }));
      }else{
        /** Update venue settings with new enabled data */
        workload.push(callAPIVenue(HTTPMethods.PUT, {
          query: venueSettings?.id!,
          params: {
            venue_id: selectedVenueId,
          },
          body: {
            tipping_enabled: !!data.tipping_enabled,
            default_tipping_option_id: data.tipping.default_option_id === "1" ? null : data.tipping.default_option_id
          },
        }));
      }
     
    }
    await Promise.all(workload);
    getTippingOptions();
  }

  function getTippingOptions(){
    const messageOptions  = venue_group_id ? {
      canAPIGetCacheValue: true,
      query:`${venue_group_id}/tipping_options`,
      params: {
        include_disabled: true,
      },
    }:{
      canAPIGetCacheValue: true,
      params: {
        venue_id: selectedVenueId,
        include_disabled: true,
      },
      selectorKeyParams: { venue_id: selectedVenueId },
    } as any;

    if(venue_group_id) {
      if(!isLoading){
        setIsLoading(true);
      }
      callGroups(HTTPMethods.GET, {
        query:venue_group_id
      }).then((response) => {
        if(response.isSuccessful && response.data){
          setGroupDetails(response.data as IVenueGroup);
        }
      }).then(()=>{
        callGroups(HTTPMethods.GET,messageOptions).then((response) =>{
          if(response.isSuccessful && response.data){
            setTippingOptions(response.data);
          }
        }).finally(()=>{
          setIsLoading(false)
        });
      })
    }else{
      callAPI(HTTPMethods.GET , messageOptions).then((response) =>{
        if(response.isSuccessful && response.data){
          setTippingOptions(response.data);
        }
      }).finally(()=>{
        setIsLoading(false)
      })
    }
  }

  return {
    isLoading,
    createTipping,
    handleSubmit,
    groupDetails,
    apiData : tippingOptions
  }
}
